// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import store from '@/store'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ImageActions } from '@/services/image'
import { CountryFlag } from '@/shared/types/countryFlag'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { imgApi } from '@/plugins/axios'
import { AxiosResponse } from 'axios'
import flags from '../../../public/flags/data.json'

const defaultImg = {
  avatar: '/img/default-avatar.png',
  card: '/img/profile/default-avatar.svg',
  event: '/img/org-default.svg',
  flag: '/img/flag-earth.png',
  org: '/img/org-default.svg',
  studio: '/img/default-studio-image.webp',
}

export function useImage() {
  function getImageById(
    id: string,
    width: number,
    height: number,
    type: 'avatar' | 'card' | 'event' | 'org' | 'flag' | 'studio' = 'avatar'
  ) {
    if (!id) return defaultImg[type]
    return `${process.env.VUE_APP_IMG_URL}/${id}/${width}/${height}`
  }

  function getAvatar(id: string, size: number) {
    return getImageById(id, size, size, 'avatar')
  }

  function getEventImg(id: string, size: number) {
    return getImageById(id, size, size, 'event')
  }

  function getOrgImage(id: string, size: number) {
    return getImageById(id, size, size, 'org')
  }

  function getStudioImage(id: string, size: number) {
    return getImageById(id, size, size, 'studio')
  }

  function getCard(id: string, size: number) {
    return ImageActions.getImageById(id, size, size, 'card')
  }

  function getFlag(countryName: string) {
    const countryFlag = flags.find((flag: CountryFlag) => flag.name === countryName)
    if (!countryFlag) return defaultImg.flag
    return countryFlag.flag
  }

  const MAX_DIMENSION = 2048
  const JPEG_QUALITY = 0.85

  async function preprocessImage(file: File): Promise<File> {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        let { naturalWidth: w, naturalHeight: h } = img

        if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
          const scale = MAX_DIMENSION / Math.max(w, h)
          w = Math.round(w * scale)
          h = Math.round(h * scale)
        }

        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)

        const outputType = file.type === 'image/png' ? 'image/webp' : 'image/jpeg'
        const ext = outputType === 'image/webp' ? '.webp' : '.jpg'

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src)
            const baseName = file.name.replace(/\.[^.]+$/, '')
            resolve(new File([blob!], baseName + ext, { type: outputType }))
          },
          outputType,
          JPEG_QUALITY
        )
      }
      img.src = URL.createObjectURL(file)
    })
  }

  async function saveImage(file: File) {
    const normalized = await preprocessImage(file)

    if (normalized.size > 10000000) {
      store.commit('SHOW_ALERT_TOAST_MESSAGE', "Use an image smaller than 10Mb, otherwise, it won't be saved.")
      setTimeout(() => store.commit('SHOW_ALERT_TOAST_MESSAGE', ''), 5000)
      return ''
    }

    const formData = new FormData()
    formData.append('file', normalized)

    const { data } = await imgApi.post<FormData, AxiosResponse<string>>('/', formData)

    return data
  }

  return {
    getAvatar,
    getEventImg,
    getOrgImage,
    getImageById,
    saveImage,
    getCard,
    getFlag,
    getStudioImage,
  }
}
